import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { getVersionFromVolume } from '@/utils'
import {
  UserCustomListModeType,
  UserCustomListTargetTypeType,
} from '../../../__generated__/graphql.ts'
import { storeToRefs } from 'pinia'
import { apolloClient } from '@/vendor/apollo-client.ts'
import { createSyncEngine } from '@/stores/sync/createSyncEngine.ts'
import { createPersistedCache } from '@/stores/sync/createPersistedCache.ts'
import { newClientId } from '@/stores/sync/clientIdentity.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { useInfoStore } from '@/stores/info'
import { useUserSyncStore, type CustomListDeltaRow } from '@/stores/user-sync'
import { useLocalizationStore } from '@/stores/localization.ts'
import { showToast } from '@/utils/showToast.ts'
import { sendError } from '@/utils/sendError.ts'
import {
  CREATE_USER_CUSTOM_LIST,
  UPDATE_USER_CUSTOM_LIST,
  DELETE_USER_CUSTOM_LIST_WITH_ITEMS,
} from '@/queries/customLists.ts'
import {
  buildOptimisticListRows,
  EMPTY_LIST_META,
  listRowKey,
  metaToCreateData,
  metaToUpdateData,
  resolveListServerIdFrom,
  type ListCrudAction,
  type ListCrudMeta,
} from '@/stores/lists.crud.ts'
import {
  applyActionStack,
  cloneListsMap,
  performMembershipSync,
  resolveNoveltyId,
  shouldRemoveMember,
  type EntryCollectionState,
  type ListAddTarget,
  type ListMember,
  type MembershipAction,
  type NoveltyMeta,
  type UserListState,
  type UserListsMap,
} from './sync.ts'

// One optimistic queue for everything the user owns on their lists — the list
// itself (create/update/delete) and its membership (add/remove) — so a create
// always drains before the item-adds that depend on its server id.
export type UserListAction = MembershipAction | ListCrudAction

function isListCrudAction(action: UserListAction): action is ListCrudAction {
  return action.op === 'create' || action.op === 'update' || action.op === 'delete'
}

export type {
  EntryCollectionState,
  ListAddTarget,
  NoveltyMeta,
  UserListState,
  UserListsMap,
} from './sync.ts'

export type ListMembershipRow = {
  id: string
  name: string
  count: number
  member: boolean
  membershipId: string | null
  pending: boolean
  eligible: boolean
  targetType: UserCustomListTargetTypeType
}

const USER_LISTS_KEY = 'userListsState'
// The pending membership queue is persisted so offline add/remove edits survive an
// app restart and reach the server once back online (mirrors user-collection).
const USER_LISTS_ACTIONS_KEY = 'userListsActionStack'

const cache = createPersistedCache<UserListState[]>(USER_LISTS_KEY, [])
const actionStackCache = createPersistedCache<UserListAction[]>(USER_LISTS_ACTIONS_KEY, [])

type RawNovelty = {
  id: string
  entryId?: string | null
  relationType?: string | null
  relationId?: string | null
  number?: string | number | null
  volumeSuffix?: string | null
  releaseItems?: unknown
  collectionVolumeInfo?: { kind?: string | null } | null
}

const BOX_RELATION_TYPE = 'collection'

// A box tracks several volume numbers (releaseItems); a single volume tracks its
// own number. Boxes are pruned via miscOwnedMap so their volumes go unused, but a
// volume novelty needs its number here or it can never count as collected.
function noveltyVolumes(novelty: RawNovelty): string[] {
  if (novelty.relationType === BOX_RELATION_TYPE) {
    return Array.isArray(novelty.releaseItems)
      ? novelty.releaseItems.map((volume) => String(volume))
      : []
  }
  const version = getVersionFromVolume({
    number: novelty.number != null ? String(novelty.number) : undefined,
    volumeSuffix: novelty.volumeSuffix,
  })
  return version ? [version] : []
}

type RawCuratedList = {
  id: string
  name?: string | null
  targetType?: UserCustomListTargetTypeType | null
  isCustomTag?: boolean | null
  customTagName?: string | null
  removeWhenCollected?: boolean | null
  removeWhenRead?: boolean | null
  curatedReleaseEntries?: ({ id: string; releaseEntry?: { id: string } | null } | null)[] | null
  curatedNovelties?: ({ id: string; novelty?: RawNovelty | null } | null)[] | null
}

function noveltyMeta(novelty: RawNovelty): NoveltyMeta {
  return {
    entryId: novelty.entryId ?? '',
    relationType: novelty.relationType ?? '',
    relationId: novelty.relationId ?? '',
    volumes: noveltyVolumes(novelty),
    collectionKind: novelty.collectionVolumeInfo?.kind ?? undefined,
  }
}

function toUserListState(list: RawCuratedList): UserListState {
  const targetType =
    list.targetType === UserCustomListTargetTypeType.Novelty
      ? UserCustomListTargetTypeType.Novelty
      : UserCustomListTargetTypeType.ReleaseEntry
  const members: ListMember[] =
    targetType === UserCustomListTargetTypeType.Novelty
      ? (list.curatedNovelties ?? []).flatMap<ListMember>((item) =>
          item?.novelty
            ? [{ entityKey: item.novelty.id, itemId: item.id, meta: noveltyMeta(item.novelty) }]
            : [],
        )
      : (list.curatedReleaseEntries ?? []).flatMap<ListMember>((item) =>
          item?.releaseEntry
            ? [{ entityKey: item.releaseEntry.id, itemId: item.id, meta: null }]
            : [],
        )
  return {
    id: list.id,
    name: list.name ?? '',
    targetType,
    isCustomTag: list.isCustomTag ?? false,
    customTagName: list.customTagName ?? '',
    removeWhenCollected: list.removeWhenCollected ?? false,
    removeWhenRead: list.removeWhenRead ?? false,
    members,
  }
}

export const useUserListsStore = defineStore('user-lists', () => {
  const authStore = useAuthStore()
  const { t } = useLocalizationStore()
  const { connected } = storeToRefs(useInfoStore())

  const userListsData = ref<UserListState[]>([])

  // Bumped whenever server-confirmed membership changes (sync fold or reload), so
  // server-backed list previews can refetch once the new item actually exists.
  const membershipRevision = ref(0)

  function persist() {
    void cache.save(userListsData.value)
  }

  const userSync = useUserSyncStore()

  // Base custom-list rows from the per-user delta feed (the config side).
  const customListBaseRows = () => userSync.customListRows

  // Resolve a list reference (urlName for a client-created list, or an existing
  // server id) to its server id, live at sync time.
  const resolveListServerId = (urlNameOrId: string): string | null =>
    resolveListServerIdFrom(urlNameOrId, customListBaseRows(), listCrudActions())

  const listCrudActions = (): ListCrudAction[] =>
    engine.actionStack.value.filter(isListCrudAction)

  // A confirmed membership edit is merged into the base state and its action
  // retired, so the materialized map stays correct without waiting for a reload.
  function foldMembershipIntoBase(action: MembershipAction) {
    const list = userListsData.value.find((entry) => entry.id === action.listId)
    if (list) {
      if (action.op === 'add') {
        if (!list.members.some((member) => member.entityKey === action.entityKey)) {
          list.members = [
            ...list.members,
            { entityKey: action.entityKey, itemId: action.itemId, meta: action.meta },
          ]
        }
      } else {
        list.members = list.members.filter((member) => member.entityKey !== action.entityKey)
      }
      persist()
    }
    engine.remove(action.id)
  }

  // One queue for the list AND its items: performSync mutates the action in place
  // (itemId / serverId) so a create resolves its server id before the item-adds
  // queued after it drain and resolve the same urlName.
  const engine = createSyncEngine<UserListAction>({
    performSync: async (action) => {
      if (isListCrudAction(action)) {
        if (action.op === 'create') {
          const { data } = await apolloClient.mutate({
            mutation: CREATE_USER_CUSTOM_LIST,
            variables: { data: metaToCreateData(action.meta, action.urlName) },
          })
          action.serverId = data?.createUserCustomList?.id ?? null
          return
        }
        const listId = resolveListServerId(action.urlName)
        if (!listId) throw new Error(`list ${action.urlName} has no server id yet`)
        if (action.op === 'update') {
          await apolloClient.mutate({
            mutation: UPDATE_USER_CUSTOM_LIST,
            variables: { id: listId, data: metaToUpdateData(action.meta) },
          })
          return
        }
        await apolloClient.mutate({
          mutation: DELETE_USER_CUSTOM_LIST_WITH_ITEMS,
          variables: { listId },
        })
        return
      }
      const listId = resolveListServerId(action.listId)
      if (!listId) throw new Error(`list ${action.listId} has no server id yet`)
      const patch = await performMembershipSync({ ...action, listId })
      if (patch) Object.assign(action, patch)
    },
    onSynced: (action) => {
      if (isListCrudAction(action)) {
        void loadCustomLists()
      } else {
        foldMembershipIntoBase(action)
      }
    },
    onError: (action, error) => {
      sendError(error, { source: 'user-lists.sync', op: action.op })
      void showToast({ text: t('addToListError'), duration: 'short' })
    },
    // Keep edits queued instead of draining them straight into a failed network
    // call (which onError would drop) whenever we're not connected — offline,
    // forced offline, or server down. Resumed once connectivity returns.
    canSync: () => connected.value,
    onChange: (actions) => void actionStackCache.save(actions),
  })

  watch(connected, (isConnected) => {
    if (!isConnected) return
    engine.resume()
    // A load that fell through while disconnected (or while the server was down)
    // left the lists unloaded; reconnecting is the moment to fetch them, or the
    // tab keeps showing nothing until the user pulls to refresh.
    if (!customListsLoaded.value) void loadCustomLists()
  })

  const membershipActions = (): MembershipAction[] =>
    engine.actionStack.value.filter((action): action is MembershipAction => !isListCrudAction(action))

  function crudMetaToListState(id: string, meta: ListCrudMeta): UserListState {
    return {
      id,
      name: meta.name,
      targetType: meta.targetType,
      isCustomTag: meta.isCustomTag,
      customTagName: meta.customTagName,
      removeWhenCollected: meta.removeWhenCollected,
      removeWhenRead: meta.removeWhenRead,
      members: [],
    }
  }

  // Lay the pending list create/update/delete overlay onto the membership base so a
  // just-created offline list is a first-class list (shows in the add-to-list sheet,
  // can receive items) before it syncs. Keyed by urlName (= id for base rows).
  function applyListCrudOverlay(map: UserListsMap) {
    for (const action of listCrudActions()) {
      if (action.synced) continue
      const existing =
        map[action.urlName] ??
        Object.values(map).find((list) => list.id === action.urlName)
      if (action.op === 'create') {
        if (!existing) map[action.urlName] = crudMetaToListState(action.urlName, action.meta)
      } else if (action.op === 'update') {
        if (existing) Object.assign(existing, crudMetaToListState(existing.id, action.meta))
      } else if (existing) {
        delete map[existing.id]
      }
    }
  }

  const listsState = computed<UserListsMap>(() => {
    const map = cloneListsMap(userListsData.value)
    applyListCrudOverlay(map)
    applyActionStack(map, membershipActions())
    return map
  })

  // The optimistic list/membership map is the source of truth. Bump the revision
  // whenever it changes — optimistically (an enqueue) or on a sync fold — so the
  // SQLite mirror re-runs and server-backed previews refetch immediately, not only
  // once the edit reaches the server. (listsState is a fresh object each recompute,
  // so this fires on any dependency change.)
  watch(listsState, () => {
    membershipRevision.value++
  })

  // Curated lists the user flagged as a custom tag, used as a novelty-overview
  // filter section ("Eigene"): each yields a tag-named toggle that filters
  // novelties down to (or out of) the list's members.
  const taggedFilterLists = computed(() =>
    Object.values(listsState.value)
      .filter((list) => list.isCustomTag && list.customTagName.trim().length > 0)
      .map((list) => ({
        id: list.id,
        tagName: list.customTagName.trim(),
        targetType: list.targetType,
      })),
  )

  function memberEntityKeysForLists(listIds: string[]): {
    noveltyIds: string[]
    releaseEntryIds: string[]
  } {
    const noveltyIds: string[] = []
    const releaseEntryIds: string[] = []
    for (const listId of listIds) {
      const list = listsState.value[listId]
      if (!list) continue
      const keys = list.members.map((member) => member.entityKey)
      if (list.targetType === UserCustomListTargetTypeType.Novelty) {
        noveltyIds.push(...keys)
      } else {
        releaseEntryIds.push(...keys)
      }
    }
    return { noveltyIds, releaseEntryIds }
  }

  function memberReleaseEntryIdsForLists(listIds: string[]): string[] {
    const releaseEntryIds = new Set<string>()
    for (const listId of listIds) {
      const list = listsState.value[listId]
      if (!list) continue
      for (const member of list.members) {
        if (list.targetType === UserCustomListTargetTypeType.Novelty) {
          if (member.meta?.entryId) releaseEntryIds.add(member.meta.entryId)
        } else {
          releaseEntryIds.add(member.entityKey)
        }
      }
    }
    return [...releaseEntryIds]
  }

  // Reconstruct the curated-list-with-members shape from the per-user delta feed:
  // curated lists + their membership items (joined by list id) — the client-side
  // equivalent of GET_MY_CURATED_LISTS_WITH_MEMBERS.
  function curatedListsFromSync(): RawCuratedList[] {
    return userSync.customListRows
      .filter((list) => list.mode === UserCustomListModeType.Curated)
      .map((list) => {
        const isNovelty = list.targetType === UserCustomListTargetTypeType.Novelty
        return {
          id: list.id,
          name: list.name,
          targetType: list.targetType,
          isCustomTag: list.isCustomTag,
          customTagName: list.customTagName,
          removeWhenCollected: list.removeWhenCollected,
          removeWhenRead: list.removeWhenRead,
          curatedReleaseEntries: isNovelty
            ? []
            : userSync.releaseEntryItemRows
                .filter((m) => m.list?.id === list.id)
                .map((m) => ({
                  id: m.id,
                  releaseEntry: m.releaseEntry?.id ? { id: m.releaseEntry.id } : null,
                })),
          curatedNovelties: isNovelty
            ? userSync.noveltyItemRows
                .filter((m) => m.list?.id === list.id)
                .map((m) => ({ id: m.id, novelty: m.novelty ?? null }))
            : [],
        }
      })
  }

  const applyUserListsFromSync = () => {
    userListsData.value = curatedListsFromSync().map(toUserListState)
    persist()
  }

  // Resolves to whether the lists now rest on real data — a fresh sync, or the
  // persisted replica when the sync was skipped/failed (offline, server down).
  // Folding an untouched empty feed into the base would persist an empty list
  // set over the cached one, so that case leaves the cache alone.
  const loadUserLists = async (): Promise<boolean> => {
    const synced = await userSync.sync()
    if (!synced && !userSync.hasReplica) return false
    applyUserListsFromSync()
    retireReconciledCrud()
    return true
  }

  // "The custom lists on screen are the user's actual lists" — the gate for
  // rendering the empty state. False keeps the empty state hidden and lets the
  // next mount / reconnect retry, so a failed load never reads as "no lists".
  const customListsLoaded = ref(false)
  const customListsLoadFailed = ref(false)

  const loadCustomLists = async () => {
    const usable = await loadUserLists()
    customListsLoaded.value = usable
    customListsLoadFailed.value = !usable
  }

  // The delta feed is shared: a sync started by another consumer (the collection)
  // lands this user's list rows too, so treat it as the load this store was
  // waiting for — otherwise a failed load stays "unloaded" until the next mount.
  watch(() => userSync.revision, () => {
    applyUserListsFromSync()
    retireReconciledCrud()
    if (!userSync.hasReplica) return
    customListsLoaded.value = true
    customListsLoadFailed.value = false
  })

  const init = async () => {
    userListsData.value = await cache.load()
    // Restore the pending queue before any base load; replace() re-drains it
    // (gated by canSync, so offline it just stays queued).
    engine.replace(await actionStackCache.load())
    if (authStore.authState?.userAdmin?.user?.id) {
      await loadCustomLists()
    } else {
      customListsLoaded.value = true
    }
  }

  // --- Custom-list configs (the list itself), owned here alongside membership ---

  // The delta base with the pending create/update/delete overlay laid on,
  // reconciled by urlName. lists.ts projects these to render-configs read-only.
  const optimisticCustomListRows = (): CustomListDeltaRow[] =>
    buildOptimisticListRows(customListBaseRows(), listCrudActions())

  const isPendingCreate = (key: string | null | undefined): boolean => {
    if (!key) return false
    const inBase = customListBaseRows().some((row) => listRowKey(row) === key)
    return !inBase && listCrudActions().some((a) => a.op === 'create' && a.urlName === key)
  }

  // Once the base delta carries a synced CRUD action's effect, drop the action.
  const retireReconciledCrud = () => {
    const baseUrlNames = new Set(customListBaseRows().map(listRowKey))
    for (const action of listCrudActions()) {
      if (!action.synced) continue
      const inBase = baseUrlNames.has(action.urlName)
      const done =
        (action.op === 'create' && inBase) ||
        (action.op === 'delete' && !inBase) ||
        action.op === 'update'
      if (done) engine.remove(action.id)
    }
  }

  const createCustomList = (meta: ListCrudMeta): string => {
    const urlName = newClientId()
    engine.enqueue({ id: newClientId(), synced: false, op: 'create', urlName, serverId: null, meta })
    return urlName
  }

  const updateCustomList = (urlName: string, meta: ListCrudMeta) => {
    engine.enqueue({ id: newClientId(), synced: false, op: 'update', urlName, serverId: null, meta })
  }

  const deleteCustomList = (urlName: string) => {
    // Created and removed before the create synced → drop the whole pending chain.
    const unsyncedCreate = listCrudActions().some(
      (a) => a.op === 'create' && a.urlName === urlName && !a.synced,
    )
    if (unsyncedCreate) {
      for (const a of engine.actionStack.value.filter(
        (a) => isListCrudAction(a) && a.urlName === urlName,
      )) {
        engine.remove(a.id)
      }
      return
    }
    engine.enqueue({
      id: newClientId(),
      synced: false,
      op: 'delete',
      urlName,
      serverId: null,
      meta: EMPTY_LIST_META,
    })
  }

  function findAction(targetType: UserCustomListTargetTypeType, entityKey: string, listId: string) {
    return membershipActions().find(
      (action) =>
        action.targetType === targetType &&
        action.entityKey === entityKey &&
        action.listId === listId,
    )
  }

  function rowsForEntity(
    targetType: UserCustomListTargetTypeType,
    entityKey: string | null,
  ): ListMembershipRow[] {
    const rows = Object.values(listsState.value).map((list) => {
      const eligible = list.targetType === targetType
      const member =
        eligible && entityKey
          ? list.members.find((entry) => entry.entityKey === entityKey)
          : undefined
      const action = eligible && entityKey ? findAction(targetType, entityKey, list.id) : undefined
      return {
        id: list.id,
        name: list.name,
        count: list.members.length,
        member: member !== undefined,
        membershipId: member?.itemId ?? null,
        pending: action !== undefined && !action.synced,
        eligible,
        targetType: list.targetType,
      }
    })
    return rows.sort((a, b) => Number(b.eligible) - Number(a.eligible))
  }

  function toggle(
    targetType: UserCustomListTargetTypeType,
    entityKey: string,
    listId: string,
    meta: NoveltyMeta | null = null,
  ) {
    const existing = findAction(targetType, entityKey, listId)
    if (existing) {
      engine.remove(existing.id)
      return
    }
    const member = listsState.value[listId]?.members.find((entry) => entry.entityKey === entityKey)
    const isMember = member !== undefined
    engine.enqueue({
      id: uuidv4(),
      synced: false,
      op: isMember ? 'remove' : 'add',
      targetType,
      listId,
      entityKey,
      itemId: isMember ? (member?.itemId ?? null) : null,
      meta: isMember ? (member?.meta ?? null) : meta,
    })
  }

  // Auto-removal target. Collapses an unsynced add (so it never reaches the
  // server); otherwise queues a delete against the server-confirmed itemId.
  function removeMember(
    member: ListMember,
    listId: string,
    targetType: UserCustomListTargetTypeType,
  ) {
    const pending = findAction(targetType, member.entityKey, listId)
    if (pending) {
      if (pending.op === 'add') engine.remove(pending.id)
      return
    }
    const baseMember = userListsData.value
      .find((list) => list.id === listId)
      ?.members.find((entry) => entry.entityKey === member.entityKey)
    if (!baseMember?.itemId) return
    engine.enqueue({
      id: uuidv4(),
      synced: false,
      op: 'remove',
      targetType,
      listId,
      entityKey: baseMember.entityKey,
      itemId: baseMember.itemId,
      meta: baseMember.meta,
    })
  }

  // A collection change on entryId just synced; drop this item from any curated
  // list configured to remove it once collected/read. Pure local decision from
  // the member metadata captured at add/prefetch — no server lookup.
  function prunedItemLabel(list: UserListState, member: ListMember): string {
    if (list.targetType === UserCustomListTargetTypeType.ReleaseEntry) {
      return t('prunedReleaseLabel')
    }
    if (member.meta?.relationType === 'collection') {
      return member.meta.collectionKind === 'bundle' ? t('collectionPrefix') : t('slipCasePrefix')
    }
    return t('volumePrefix')
  }

  function pruneRemovedForEntry(entryId: string, state: EntryCollectionState) {
    for (const list of Object.values(listsState.value)) {
      if (!list.removeWhenCollected && !list.removeWhenRead) continue
      for (const member of list.members) {
        const matchesEntry =
          list.targetType === UserCustomListTargetTypeType.ReleaseEntry
            ? member.entityKey === entryId
            : member.meta?.entryId === entryId
        if (!matchesEntry) continue
        if (shouldRemoveMember(list, member, state)) {
          removeMember(member, list.id, list.targetType)
          void showToast({
            text: t('listItemPrunedToast', {
              item: prunedItemLabel(list, member),
              list: list.name,
            }),
            duration: 'short',
          })
        }
      }
    }
  }

  async function entityKeyFor(target: ListAddTarget): Promise<string | null> {
    if (target.targetType === UserCustomListTargetTypeType.Novelty) {
      return target.relationId ? resolveNoveltyId(target.relationId) : null
    }
    return target.releaseEntryId ?? null
  }

  async function enqueueAddToList(target: ListAddTarget, listId: string) {
    const entityKey = await entityKeyFor(target)
    if (!entityKey) return
    if (listsState.value[listId]?.members.some((member) => member.entityKey === entityKey)) return
    engine.enqueue({
      id: uuidv4(),
      synced: false,
      op: 'add',
      targetType: target.targetType,
      listId,
      entityKey,
      itemId: null,
      meta: target.meta ?? null,
    })
  }

  return {
    init,
    loadUserLists,
    loadCustomLists,
    customListsLoaded,
    customListsLoadFailed,
    optimisticCustomListRows,
    isPendingCreate,
    resolveListServerId,
    createCustomList,
    updateCustomList,
    deleteCustomList,
    listsState,
    membershipRevision,
    taggedFilterLists,
    memberEntityKeysForLists,
    memberReleaseEntryIdsForLists,
    rowsForEntity,
    toggle,
    entityKeyFor,
    enqueueAddToList,
    pruneRemovedForEntry,
  }
})
